import { useEffect } from "react";

export default function BidCalculatorPRDPage() {
  useEffect(() => {
    document.title = "강스터디 - 경매 입찰가 계산기 PRD";
  }, []);

  return (
    <>
      {/* Print-only styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 18mm 15mm 20mm 15mm;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="min-h-screen bg-white font-['Noto_Sans_KR'] text-[#1C1917] print:bg-white">
        <div className="max-w-[210mm] mx-auto px-8 py-10 print:px-0 print:py-0">

          {/* Print button - hidden in print */}
          <div className="mb-8 print:hidden">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#1C1917] text-white text-sm font-semibold rounded-lg hover:bg-[#3A2E28] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line"></i>
              PDF로 저장하기 (Ctrl+P)
            </button>
            <p className="text-xs text-[#8C8780] mt-2">
              인쇄 대화상자에서 "PDF로 저장"을 선택하세요
            </p>
          </div>

          {/* ===== COVER / HEADER ===== */}
          <div className="mb-10 pb-8 border-b-2 border-[#1C1917]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs font-semibold text-[#C4956A] uppercase tracking-widest mb-2">
                  Product Requirements Document
                </p>
                <h1 className="text-3xl font-bold text-[#1C1917] leading-tight">
                  경매 입찰가 계산기
                </h1>
                <p className="text-lg text-[#5C5650] mt-1">Bid Calculator — Dual Mode Auction Pricing Tool</p>
              </div>
              <div className="text-right text-xs text-[#8C8780] space-y-0.5">
                <p>버전 1.0</p>
                <p>작성일 2026-08-07</p>
                <p>프로젝트 강스터디</p>
              </div>
            </div>

            {/* Meta table */}
            <div className="grid grid-cols-2 gap-0 text-sm border-t border-[#E8E5E0] pt-6">
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-16 flex-shrink-0">제품명</span>
                <span className="font-semibold">경매 입찰가 계산기 (Bid Calculator)</span>
              </div>
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-16 flex-shrink-0">구분</span>
                <span className="font-semibold">웹 애플리케이션 (SPA)</span>
              </div>
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-16 flex-shrink-0">대상</span>
                <span className="font-semibold">경매 투자자 (초보~전문가)</span>
              </div>
              <div className="flex gap-4 py-1">
                <span className="text-[#8C8780] w-16 flex-shrink-0">상태</span>
                <span className="font-semibold text-emerald-600">개발 완료 (v1.0)</span>
              </div>
            </div>
          </div>

          {/* ===== 1. 개요 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">
              1. 제품 개요
            </h2>
            <div className="space-y-3 text-sm text-[#5C5650] leading-relaxed">
              <p>
                <strong className="text-[#1C1917]">경매 입찰가 계산기</strong>는 경매 투자자가
                물건의 적정 입찰가를 산출할 수 있도록 설계된 <strong className="text-[#1C1917]">듀얼 모드 계산 도구</strong>입니다.
                일반방식(비용차감형)과 시세역산방식(Top-Down Pricing) 두 가지 접근법을 제공하여,
                초보자는 물론 전문 투자자까지 자신의 투자 전략에 맞는 입찰가를 도출할 수 있습니다.
              </p>
            </div>
          </section>

          {/* ===== 2. 핵심 기능 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">
              2. 핵심 기능
            </h2>

            {/* 2.1 일반방식 */}
            <div className="mb-8">
              <h3 className="text-base font-bold text-[#C4956A] mb-3">
                2.1 일반방식 (비용차감형)
              </h3>
              <p className="text-sm text-[#5C5650] mb-4 leading-relaxed">
                매각 시세에서 인수 권리금, 부대비용, 목표 수익을 순차적으로 차감하여
                <strong className="text-[#1C1917]"> 3단계 적정 입찰가</strong>를 산출합니다.
                경매 초보자도 직관적으로 이해할 수 있는 방식입니다.
              </p>

              <h4 className="text-xs font-bold text-[#1C1917] uppercase tracking-wide mb-2">입력 항목</h4>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#FAFAF8] border-b border-[#E8E5E0]">
                      <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">섹션</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">필드</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">설명</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">기본값</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#5C5650]">
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]" rowSpan={3}>① 물건 기본 정보</td>
                      <td className="py-2 px-3">물건 종류</td>
                      <td className="py-2 px-3">아파트 / 다세대연립 / 오피스텔 / 단독주택 / 상가</td>
                      <td className="py-2 px-3">아파트</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3">전용면적</td>
                      <td className="py-2 px-3">m² 단위</td>
                      <td className="py-2 px-3">85 m²</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3">건물 연식</td>
                      <td className="py-2 px-3">준공일 기준</td>
                      <td className="py-2 px-3">15년</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]" rowSpan={2}>② 시세 정보</td>
                      <td className="py-2 px-3">추정 매각 시세 (Vest)</td>
                      <td className="py-2 px-3">인근 급매가·실거래가 기준</td>
                      <td className="py-2 px-3">3.8억</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3">지역 평균 낙찰가율</td>
                      <td className="py-2 px-3">해당 지역 최근 6개월 평균</td>
                      <td className="py-2 px-3">85%</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]" rowSpan={2}>③ 권리·점유 정보</td>
                      <td className="py-2 px-3">인수 권리금액 (R)</td>
                      <td className="py-2 px-3">대항력 임차인 보증금, 유치권 등</td>
                      <td className="py-2 px-3">0원</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3">점유자 유형</td>
                      <td className="py-2 px-3">없음 / 채무자 / 임차인(대항력 無/有) / 유치권자</td>
                      <td className="py-2 px-3">없음</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]" rowSpan={3}>④ 세금·금융</td>
                      <td className="py-2 px-3">보유 주택 수</td>
                      <td className="py-2 px-3">취득세율 결정 (0:1.1~3.5%, 1:1.1~3.5%, 2:8%, 3+:12%)</td>
                      <td className="py-2 px-3">1주택</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3">대출 예정 금액</td>
                      <td className="py-2 px-3">-</td>
                      <td className="py-2 px-3">1.5억</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3">대출 금리 / 소요 기간</td>
                      <td className="py-2 px-3">이자 비용 산출용</td>
                      <td className="py-2 px-3">4.5% / 6개월</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]" rowSpan={2}>⑤ 기타 비용·목표</td>
                      <td className="py-2 px-3">미납 관리비 (Cmgt)</td>
                      <td className="py-2 px-3">미입력 시 면적 기준 자동 추정</td>
                      <td className="py-2 px-3">150만원</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3">목표 순수익 (Mtarget)</td>
                      <td className="py-2 px-3">이 거래에서 얻고자 하는 최소 순수익</td>
                      <td className="py-2 px-3">5,000만원</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h4 className="text-xs font-bold text-[#1C1917] uppercase tracking-wide mb-2">연산 공식</h4>
              <div className="bg-[#FAFAF8] border border-[#E8E5E0] rounded-lg p-4 mb-3 font-mono text-xs text-[#5C5650] leading-relaxed">
                <p className="mb-1">수리비(Crep) = 전용면적 × 50,000 × 연식가중치 (≤5년:0.5, ≤10년:1.0, ≤20년:1.5, {">"}20년:2.0)</p>
                <p className="mb-1">명도비 = 점유자유형별 추정 (채무자: 200만원~, 임차인: 300만원~)</p>
                <p className="mb-1">취득세(Ctax) = Vest × 세율 (주택수·가격구간별 차등)</p>
                <p className="mb-1">금융비용(Cfin) = 대출금액 × 금리 × (소요기간/12)</p>
                <p className="mb-1">총부대비용(C) = Crep + 명도비 + Ctax + Cfin + Cmgt</p>
                <p className="font-bold text-[#1C1917] mt-2 pt-2 border-t border-[#E8E5E0]">
                  Pmax = Vest − (R + C + Mtarget)
                </p>
              </div>

              <h4 className="text-xs font-bold text-[#1C1917] uppercase tracking-wide mb-2">산출물 — 3단계 입찰가</h4>
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-emerald-700 mb-1">보수적 입찰가</p>
                  <p className="text-[10px] text-emerald-600">Safe Bid</p>
                  <p className="text-[10px] text-[#5C5650] mt-1">Pmax × 0.95</p>
                  <p className="text-[10px] text-[#8C8780] mt-0.5">수익률 극대화, 낙찰률 낮음</p>
                </div>
                <div className="bg-[#C4956A]/10 border border-[#C4956A]/30 rounded-lg p-3">
                  <p className="text-xs font-bold text-[#C4956A] mb-1">적정 추천가 ★</p>
                  <p className="text-[10px] text-[#B8A08A]">Recommended Bid</p>
                  <p className="text-[10px] text-[#5C5650] mt-1">min(Pmax, V×낙찰가율)</p>
                  <p className="text-[10px] text-[#8C8780] mt-0.5">낙찰률·수익률 최적 밸런스</p>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-rose-700 mb-1">공격적 입찰가</p>
                  <p className="text-[10px] text-rose-500">Aggressive Bid</p>
                  <p className="text-[10px] text-[#5C5650] mt-1">Pmax</p>
                  <p className="text-[10px] text-[#8C8780] mt-0.5">낙찰률 최대, 수익 최소</p>
                </div>
              </div>
            </div>

            {/* 2.2 시세역산방식 */}
            <div>
              <h3 className="text-base font-bold text-[#C4956A] mb-3">
                2.2 시세역산방식 (Top-Down Pricing) <span className="text-xs bg-[#C4956A]/10 text-[#C4956A] px-1.5 py-0.5 rounded ml-1 font-semibold">NEW</span>
              </h3>
              <p className="text-sm text-[#5C5650] mb-4 leading-relaxed">
                <strong className="text-[#1C1917]">감정가 착시를 제거</strong>하고, 실제 팔 수 있는 급매 시세에서 출발하여
                목표 수익·비용·인수 권리금을 역으로 차감해 <strong className="text-[#1C1917]">손실 없는 투찰 상한가</strong>를 도출합니다.
                경매 고수·매매사업자에게 특히 유용한 방식입니다.
              </p>

              <h4 className="text-xs font-bold text-[#1C1917] uppercase tracking-wide mb-2">핵심 입력 — 급매 시세 (V)</h4>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#FAFAF8] border-b border-[#E8E5E0]">
                      <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">섹션</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">필드</th>
                      <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">설명</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#5C5650]">
                    <tr className="border-b border-[#F0EDE8] bg-[#C4956A]/5">
                      <td className="py-2 px-3 font-medium text-[#C4956A]">② 시세 정보 ★</td>
                      <td className="py-2 px-3 font-semibold">급매 시세 (V)</td>
                      <td className="py-2 px-3">국토부·KB 기준 인근 최저 거래가 — 역산의 출발점</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3"></td>
                      <td className="py-2 px-3 text-[#8C8780]">법원 감정가 (참고)</td>
                      <td className="py-2 px-3 text-[#8C8780]">계산 미반영, 급매가와의 괴리 확인용</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]">③ 목표 마진</td>
                      <td className="py-2 px-3">목표 순수익 (M)</td>
                      <td className="py-2 px-3">금액 직접 입력 / ROI(%) 입력 듀얼 모드</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]">④ 권리·점유</td>
                      <td className="py-2 px-3">인수 권리금액 (R)</td>
                      <td className="py-2 px-3">대항력 임차인·유치권 등 반드시 인수해야 할 금액</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]">⑤ 세금·금융 (C)</td>
                      <td className="py-2 px-3">보유주택·대출·금리·기간</td>
                      <td className="py-2 px-3">일반방식과 동일한 로직</td>
                    </tr>
                    <tr className="border-b border-[#F0EDE8]">
                      <td className="py-2 px-3 font-medium text-[#1C1917]">⑥ 기타 비용 (C)</td>
                      <td className="py-2 px-3">미납관리비·중개수수료·기타</td>
                      <td className="py-2 px-3">중개수수료·등기비 등 직접비용 추가 반영</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h4 className="text-xs font-bold text-[#1C1917] uppercase tracking-wide mb-2">핵심 공식</h4>
              <div className="bg-[#1C1917] text-white rounded-lg p-4 mb-3 font-mono text-sm text-center font-bold">
                P = V − M − C − R
              </div>
              <p className="text-xs text-[#5C5650] mb-4">V=급매시세 | M=목표마진 | C=제반비용합계 | R=인수권리금</p>

              <h4 className="text-xs font-bold text-[#1C1917] uppercase tracking-wide mb-2">차별화 기능</h4>
              <div className="space-y-2 text-xs text-[#5C5650]">
                <div className="flex items-start gap-2">
                  <i className="ri-contrast-2-line text-[#C4956A] mt-0.5"></i>
                  <span><strong className="text-[#1C1917]">실시간 슬라이더</strong> — 목표 수익 조절 시 투찰 상한가 즉시 역산</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-alarm-warning-line text-rose-500 mt-0.5"></i>
                  <span><strong className="text-[#1C1917]">위험 알림</strong> — 마지노선 초과(투찰불가) / 최저입찰가 미달 경고</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-grid-line text-[#C4956A] mt-0.5"></i>
                  <span><strong className="text-[#1C1917]">민감도 분석</strong> — 시세 ±10%, 5단계 시나리오 매트릭스</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-bar-chart-horizontal-line text-[#C4956A] mt-0.5"></i>
                  <span><strong className="text-[#1C1917]">역산 구조 시각화</strong> — V→M→C→R→P 바 차트로 직관적 이해</span>
                </div>
              </div>
            </div>
          </section>

          {/* ===== 3. 페이지 구조 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">
              3. 페이지 구성
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#FAFAF8] border-b border-[#E8E5E0]">
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917] w-40">페이지</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">라우트</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">설명</th>
                  </tr>
                </thead>
                <tbody className="text-[#5C5650]">
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">입찰가 계산기</td>
                    <td className="py-2 px-3 font-mono text-[10px]">/bid-calculator</td>
                    <td className="py-2 px-3">듀얼 모드(일반+시세역산) 탭 전환, 실시간 결과</td>
                  </tr>
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">계산법 설명서</td>
                    <td className="py-2 px-3 font-mono text-[10px]">/bid-calculator-guide</td>
                    <td className="py-2 px-3">두 방식의 개념·공식·예시를 상세히 설명하는 가이드</td>
                  </tr>
                  <tr className="border-b border-[#F0EDE8]">
                    <td className="py-2 px-3 font-medium text-[#1C1917]">물건 비교 계산기</td>
                    <td className="py-2 px-3 font-mono text-[10px]">/compare</td>
                    <td className="py-2 px-3">여러 경매 물건을 나란히 비교 분석</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== 4. 연동 관계 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">
              4. 교차 연동
            </h2>
            <div className="space-y-3 text-sm text-[#5C5650]">
              <div className="flex items-start gap-2">
                <i className="ri-auction-line text-[#C4956A] mt-0.5"></i>
                <div>
                  <p className="font-semibold text-[#1C1917]">경매물건 → 계산기 Pre-fill</p>
                  <p className="text-xs">경매물건 상세 페이지에서 "입찰가 계산하기" 클릭 시,
                    해당 물건의 데이터가 <span className="font-mono text-[10px]">?itemId=xxx</span> 쿼리 파라미터로 전달되어
                    일반방식·시세역산방식 양쪽 폼에 자동 입력됩니다.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-brain-line text-[#C4956A] mt-0.5"></i>
                <div>
                  <p className="font-semibold text-[#1C1917]">AI 투자 시뮬레이터 연결</p>
                  <p className="text-xs">결과 패널 하단 CTA — 순이익·ROE·투자등급까지 심층 분석 연계</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-file-search-line text-[#C4956A] mt-0.5"></i>
                <div>
                  <p className="font-semibold text-[#1C1917]">리포트·상담 연계</p>
                  <p className="text-xs">하단 링크 섹션 — 물건분석 리포트, 1:1 전문가 상담으로 연결</p>
                </div>
              </div>
            </div>
          </section>

          {/* ===== 5. 기술 스택 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">
              5. 기술 스택
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#FAFAF8] border-b border-[#E8E5E0]">
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">구분</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#1C1917]">기술</th>
                  </tr>
                </thead>
                <tbody className="text-[#5C5650]">
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">프레임워크</td><td className="py-2 px-3">React 19 + TypeScript</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">스타일링</td><td className="py-2 px-3">Tailwind CSS v3</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">애니메이션</td><td className="py-2 px-3">Motion (Framer Motion)</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">라우팅</td><td className="py-2 px-3">React Router v7</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">아이콘</td><td className="py-2 px-3">Remix Icon</td></tr>
                  <tr className="border-b border-[#F0EDE8]"><td className="py-2 px-3 font-medium">폰트</td><td className="py-2 px-3">Noto Sans KR (Google Fonts)</td></tr>
                  <tr><td className="py-2 px-3 font-medium">상태 관리</td><td className="py-2 px-3">React useState + useMemo</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== 6. 컴포넌트 구조 ===== */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-[#1C1917] border-b border-[#E8E5E0] pb-2 mb-4">
              6. 컴포넌트 구조
            </h2>
            <div className="bg-[#FAFAF8] border border-[#E8E5E0] rounded-lg p-4 font-mono text-xs text-[#5C5650] leading-relaxed">
              <p>src/pages/bid-calculator/</p>
              <p className="ml-4">├── page.tsx          — 메인 페이지 (탭, 레이아웃, 라우팅)</p>
              <p className="ml-4">├── components/</p>
              <p className="ml-8">├── BidForm.tsx      — 일반방식 입력 폼 (5섹션)</p>
              <p className="ml-8">├── BidResult.tsx    — 일반방식 결과 (3단계 + 비용내역 + 공식)</p>
              <p className="ml-8">├── ReverseForm.tsx  — 시세역산 입력 폼 (6섹션)</p>
              <p className="ml-8">└── ReverseResult.tsx — 시세역산 결과 (상한가 + 민감도 + 슬라이더)</p>
            </div>
          </section>

          {/* ===== Footer ===== */}
          <div className="border-t border-[#E8E5E0] pt-6 text-xs text-[#8C8780]">
            <p>문서 버전 1.0 | 2026-08-07 | 강스터디 (kangstudy.kr)</p>
            <p className="mt-1">본 문서는 경매 입찰가 계산기 v1.0의 제품 요구사항 정의서입니다.</p>
          </div>

        </div>
      </div>
    </>
  );
}